import {RouteReuseStrategy} from '@angular/router';
import {BrowserModule} from '@angular/platform-browser';
import {LOCALE_ID, NgModule} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {StoreModule} from '@ngrx/store';
import {metaReducers, reducers} from './reducers';
import {EffectsModule} from '@ngrx/effects';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {environment} from '../environments/environment';
import {AuthModule} from './auth/auth.module';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule,} from '@angular/material-moment-adapter';
import {ServiceWorkerModule} from '@angular/service-worker';
import {registerLocaleData} from '@angular/common';
import localeIt from '@angular/common/locales/it';
import {getApp, initializeApp, provideFirebaseApp} from '@angular/fire/app';
import {connectAuthEmulator, getAuth, provideAuth} from '@angular/fire/auth';
import {connectFirestoreEmulator, getFirestore, provideFirestore} from '@angular/fire/firestore';
import {getMessaging, provideMessaging} from '@angular/fire/messaging';
import {AngularFireModule} from '@angular/fire/compat';
import {AngularFireMessagingModule} from '@angular/fire/compat/messaging';
import {USE_EMULATOR as USE_AUTH_EMULATOR} from '@angular/fire/compat/auth';
import {USE_EMULATOR as USE_FIRESTORE_EMULATOR} from '@angular/fire/compat/firestore';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {ArchiveModule} from "./archive/archive.module";
import {NgxCaptchaModule} from "ngx-captcha";
import {MatDatepickerAdapter} from "./shared/adapters/mat-datepicker-adapter.adapter";

registerLocaleData(localeIt);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgxCaptchaModule,
    BrowserAnimationsModule,
    AuthModule,
    ArchiveModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
    }),
    MatDatepickerModule,
    MatMomentDateModule,
    MatSlideToggleModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.PWA_ENABLED,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireMessagingModule,
    IonicModule.forRoot(),
  ],
  providers: [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      if (environment.firebase.useEmulators && environment.firebase['emulators']) {
        const emulators = environment.firebase['emulators'] as any;
        connectAuthEmulator(auth, `http://${emulators.auth.host}:${emulators.auth.port}`, { disableWarnings: true });
      }
      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.firebase.useEmulators && environment.firebase['emulators']) {
        const emulators = environment.firebase['emulators'] as any;
        connectFirestoreEmulator(firestore, emulators.firestore.host, emulators.firestore.port);
      }
      return firestore;
    }),
    provideMessaging(() => getMessaging()),
    // Configure emulators for compat mode (AngularFireAuth, AngularFirestore)
    ...(environment.firebase.useEmulators && environment.firebase['emulators'] ? (() => {
      const emulators = environment.firebase['emulators'] as any;
      return [
        { provide: USE_AUTH_EMULATOR, useValue: [`http://${emulators.auth.host}:${emulators.auth.port}`] },
        { provide: USE_FIRESTORE_EMULATOR, useValue: [emulators.firestore.host, emulators.firestore.port] }
      ];
    })() : []),
    { provide: LOCALE_ID, useValue: "it" },
    { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { strict: true } },
    { provide: DateAdapter, useClass: MatDatepickerAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: ['l', 'LL', 'DD/MM/YYYY'],
        },
        display: {
          dateInput: 'DD/MM/YYYY',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: 'googleTagManagerId', useValue: environment.GOOGLE_TAG_ID }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
